'use client'

import { useState } from 'react'
import ProductImageGallery from './ProductImageGallery'
import ProductPurchaseSection from './ProductPurchaseSection'
import ColorSamplePreview from './ColorSamplePreview'
import type { Product, ProductImage, Color } from '@/lib/types'

interface ProductPageContentProps {
  product: Product
  images: ProductImage[]
  colors: Color[]
}

export default function ProductPageContent({
  product,
  images,
  colors,
}: ProductPageContentProps) {
  const [selectedColor, setSelectedColor] = useState<Color | null>(colors[0] || null)

  return (
    <div className="grid lg:grid-cols-2 gap-10 lg:gap-14">
      {/* Left Column - Images */}
      <div className="space-y-6">
        {/* Main Product Image */}
        <div className="bg-white rounded-3xl shadow-soft p-6">
          <ProductImageGallery images={images} />
        </div>

        {/* Color Sample Preview - Shows under main image */}
        <ColorSamplePreview selectedColor={selectedColor} />
      </div>

      {/* Right Column - Product Info */}
      <div className="space-y-6">
        <div className="bg-white rounded-3xl shadow-soft p-6">
          <h1 className="text-3xl md:text-4xl font-bold mb-3">{product.name}</h1>
          <p className="text-3xl font-bold text-primary mb-4">
            ${(product.price_cents / 100).toFixed(2)}
          </p>
          <div className="prose prose-sm text-gray-600">
            {product.description
              .split('\n')
              .filter(Boolean)
              .map((paragraph, index) => (
                <p key={index} className="mb-4 last:mb-0">{paragraph}</p>
              ))}
          </div>
        </div>

        {/* Color Selection & Add to Cart */}
        <ProductPurchaseSection
          product={product}
          colors={colors}
          onColorChange={setSelectedColor}
        />

        {/* Safety Notice */}
        <div className="p-5 bg-cloud border border-yellow-100 rounded-3xl shadow-soft">
          <p className="text-sm text-yellow-900/80">
            <strong>Safety:</strong> Small parts may pose choking hazards for children under 3.
            Parent/guardian supervision required.
          </p>
        </div>
      </div>
    </div>
  )
}
